import React, { useEffect, useRef, useState } from 'react';
import { Motion, PlainStyle, spring, Style } from 'react-motion';
import { styled } from 'theme';
import { alpha, Box, Button, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import Section from '../Section';
import Typography from '@mui/material/Typography';
import { useShadowStyle } from 'hooks/useShadowStyle';
import { useAppSelector } from 'hooks/useAppSelector';

interface CollapsableBarContainerProps {
    top?: number;
    height: number;
}

const CollapsableBarContainer = styled('div')<CollapsableBarContainerProps>(({ top, height, theme }) => ({
    zIndex: theme.zIndex.appBar + 1,
    position: 'absolute',
    top: top ? `${top}px` : '0px',
    left: 0,
    right: 0,
    height: `${height}px`,
    overflow: 'hidden',
    backgroundColor: alpha(theme.palette.background.default, 0.9),
}));

interface CollapsableBarProps {
    children: React.ReactNode;
    isOpen: boolean;
    isSubSectionOpen?: boolean;
    onClose?: () => void;
    top: number;
    subSection?: React.ReactNode;
    type: string;
}

const CollapsableBar = ({
    children,
    isOpen,
    isSubSectionOpen,
    onClose,
    top,
    subSection,
    ...props
}: CollapsableBarProps) => {
    const theme = useTheme();
    const shadowStyle = useShadowStyle();
    const locationData = useAppSelector((state) => state.location);
    const {
        locationByNameSuccess,
        places,
    } = locationData;
    const sectionRef = useRef<HTMLDivElement>(null);
    const subSectionRef = useRef<HTMLDivElement>(null);
    const [dynamicHeightSection, setDynamicHeightSection] = useState(0);
    const [dynamicHeightSubSection, setDynamicHeightSubSection] = useState(0);

    // This is a workaround to get the right sizes on resize.
    // It will produce a glitch in the search bar when resizing, but it's the way
    // I found to be able to get the actual scrollHeight on
    // sectionRef and subSectionRef as calculated by the browser

    let debounceTimeout: NodeJS.Timeout | null = null;

    const handleResize = (time = 500) => {
        clearTimeout(debounceTimeout);
        clearHeight();
        debounceTimeout = setTimeout(() => {
            updateHeight();
        }, time);
    };

    const clearHeight = () => {
        if (sectionRef.current) {
            setDynamicHeightSection(0);
        }
        if (subSectionRef.current) {
            setDynamicHeightSubSection(0);
        }
    };

    const updateHeight = () => {
        if (sectionRef.current) {
            setDynamicHeightSection(sectionRef.current.scrollHeight);
        }
        if (subSectionRef.current) {
            setDynamicHeightSubSection(subSectionRef.current.scrollHeight);
        }
    };

    useEffect(() => {
        updateHeight();
        window.addEventListener('resize', () => handleResize(0));
        return () => {
            window.removeEventListener('resize', () => handleResize(0));
            clearTimeout(debounceTimeout);
        };
    }, []);

    useEffect(() => {
        updateHeight();
    }, [children]);

    useEffect(() => {
        handleResize();
    }, [locationByNameSuccess, places])

    const defaultStyle: PlainStyle = {
        heightMain: 0,
        heightSecondary: 0,
    };

    const style: Style = {
        heightMain: spring(isOpen ? dynamicHeightSection : 0),
        heightSecondary: spring(isSubSectionOpen ? dynamicHeightSubSection : 0),
    };

    return (
        <Motion
            defaultStyle={defaultStyle}
            style={style}
        >
            {({ heightMain, heightSecondary }) => (
                <>
                    <CollapsableBarContainer
                        ref={sectionRef}
                        height={heightMain}
                        top={top}
                        {...props}
                    >
                        {onClose ? (
                            <Box p={1.5}>
                                <Button onClick={onClose} color={'secondary'} variant='contained'>
                                    <Close
                                        sx={{
                                            ...shadowStyle,
                                            fontSize: 20,
                                            color: theme.palette.secondary.contrastText,
                                        }}
                                    />
                                    <Typography variant={'body1'}>Close</Typography>
                                </Button>
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                mt: 0,
                                width: 'calc(100% - 2em)',
                                mx: 'auto'
                            }}
                        >
                            {children}
                        </Box>
                    </CollapsableBarContainer>
                    {subSection ? (
                        <CollapsableBarContainer
                            ref={subSectionRef}
                            height={heightSecondary}
                            top={top + heightMain}
                            {...props}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Section>{subSection}</Section>
                            </Box>
                        </CollapsableBarContainer>
                    ) : null}
                </>
            )}
        </Motion>
    );
};

export default CollapsableBar;
